import { useState } from 'react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { ResponsiveSelect } from '../ui/ResponsiveSelect';

interface CacheConfigDialogProps {
  cache: {
    name: string;
    description: string;
    defaultTtl: number;
    activeTtl: number;
    enabled: boolean;
    connectTimeoutSeconds?: number;
    callTimeoutSeconds?: number;
    inactiveRefreshMode?: 'never' | 'daily' | 'interval';
    inactiveRefreshDailyTimes?: string[];
    inactiveRefreshIntervalSeconds?: number | null;
    activeRefreshMode?: 'never' | 'interval';
    activeRefreshIntervalSeconds?: number | null;
  };
  onClose: () => void;
  onSave: (updates: {
    defaultTtl?: number;
    activeTtl?: number;
    enabled?: boolean;
    connectTimeoutSeconds?: number;
    callTimeoutSeconds?: number;
    inactiveRefreshMode?: 'never' | 'daily' | 'interval';
    inactiveRefreshDailyTimes?: string[];
    inactiveRefreshIntervalSeconds?: number;
    activeRefreshMode?: 'never' | 'interval';
    activeRefreshIntervalSeconds?: number;
  }) => Promise<void>;
}

export function CacheConfigDialog({ cache, onClose, onSave }: CacheConfigDialogProps) {
  const [defaultTtl, setDefaultTtl] = useState(cache.defaultTtl);
  const [activeTtl, setActiveTtl] = useState(cache.activeTtl);
  const [enabled, setEnabled] = useState(cache.enabled);
  const [connectTimeoutSeconds, setConnectTimeoutSeconds] = useState<number>(
    Math.max(1, Math.min(200, Number(cache.connectTimeoutSeconds ?? 2) || 2))
  );
  const [callTimeoutSeconds, setCallTimeoutSeconds] = useState<number>(
    Math.max(1, Math.min(200, Number(cache.callTimeoutSeconds ?? 10) || 10))
  );
  const [inactiveRefreshMode, setInactiveRefreshMode] = useState<'never' | 'daily' | 'interval'>(
    cache.inactiveRefreshMode || 'never'
  );
  const [inactiveDailyTimesText, setInactiveDailyTimesText] = useState(
    (cache.inactiveRefreshDailyTimes || []).join(', ')
  );
  const [inactiveInterval, setInactiveInterval] = useState<number>(
    Number(cache.inactiveRefreshIntervalSeconds || 0)
  );
  const [activeRefreshMode, setActiveRefreshMode] = useState<'never' | 'interval'>(
    cache.activeRefreshMode || 'never'
  );
  const [activeInterval, setActiveInterval] = useState<number>(
    Number(cache.activeRefreshIntervalSeconds || 0)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (defaultTtl < 0) {
      setError('Default TTL must be 0 (never expires) or greater');
      return;
    }

    if (activeTtl < 0) {
      setError('Active TTL must be 0 (never expires) or greater');
      return;
    }

    if (defaultTtl === 0 && activeTtl !== 0) {
      setError('If Default TTL is set to never expire (0), Active TTL must also be 0');
      return;
    }

    if (defaultTtl > 0 && activeTtl > defaultTtl) {
      setError('Active TTL should not exceed default TTL (unless Default TTL is 0 / never expires)');
      return;
    }

    // Refresh policy validation
    if (!Number.isFinite(connectTimeoutSeconds) || connectTimeoutSeconds < 1 || connectTimeoutSeconds > 200) {
      setError('Connect timeout must be between 1 and 200 seconds');
      return;
    }
    if (!Number.isFinite(callTimeoutSeconds) || callTimeoutSeconds < 1 || callTimeoutSeconds > 200) {
      setError('Call timeout must be between 1 and 200 seconds');
      return;
    }
    if (inactiveRefreshMode === 'interval' && inactiveInterval < 10) {
      setError('Inactive refresh interval must be at least 10 seconds');
      return;
    }
    if (activeRefreshMode === 'interval' && activeInterval < 10) {
      setError('Active refresh interval must be at least 10 seconds');
      return;
    }
    if (inactiveRefreshMode === 'daily') {
      const parts = inactiveDailyTimesText
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const invalid = parts.find(t => !/^\d{2}:\d{2}$/.test(t));
      if (invalid) {
        setError(`Invalid daily time "${invalid}". Use HH:MM (24h), comma-separated.`);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave({
        defaultTtl,
        activeTtl,
        enabled,
        connectTimeoutSeconds,
        callTimeoutSeconds,
        inactiveRefreshMode,
        inactiveRefreshDailyTimes:
          inactiveRefreshMode === 'daily'
            ? inactiveDailyTimesText
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
            : [],
        inactiveRefreshIntervalSeconds: inactiveRefreshMode === 'interval' ? inactiveInterval : undefined,
        activeRefreshMode,
        activeRefreshIntervalSeconds: activeRefreshMode === 'interval' ? activeInterval : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const formatTtlHelp = (seconds: number): string => {
    if (seconds === 0) return 'Never expires';
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours} hours ${mins} minutes` : `${hours} hours`;
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent maxWidth="md">
        <DialogHeader>
          <DialogTitle>Configure Cache: {cache.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <DialogBody>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">{cache.description}</p>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Default TTL (when no users active)
                </label>
                <input
                  type="number"
                  min="0"
                  value={defaultTtl}
                  onChange={(e) => setDefaultTtl(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formatTtlHelp(defaultTtl)} - Cache duration when page is inactive
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Active TTL (when users are active)
                </label>
                <input
                  type="number"
                  min="0"
                  value={activeTtl}
                  onChange={(e) => setActiveTtl(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formatTtlHelp(activeTtl)} - Cache duration when users are actively viewing
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Cache Enabled
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Disable to bypass this cache
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  External Call Timeouts
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Connect timeout (seconds)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={connectTimeoutSeconds}
                      onChange={(e) => setConnectTimeoutSeconds(Math.max(1, Math.min(200, parseInt(e.target.value) || 0)))}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Time allowed to establish connection / receive headers.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Call timeout (seconds)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={callTimeoutSeconds}
                      onChange={(e) => setCallTimeoutSeconds(Math.max(1, Math.min(200, parseInt(e.target.value) || 0)))}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Time allowed for response body transfer after connected.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Refresh Policy
                </h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      When no subscribers (inactive)
                    </label>
                    <ResponsiveSelect
                      label="Inactive refresh mode"
                      value={inactiveRefreshMode}
                      onChange={(value) => setInactiveRefreshMode(value as any)}
                      disabled={saving}
                      className="w-full"
                      options={[
                        { value: 'never', label: 'Never refresh' },
                        { value: 'daily', label: 'Refresh at times of day' },
                        { value: 'interval', label: 'Refresh every X seconds' },
                      ]}
                    />
                  </div>

                  {inactiveRefreshMode === 'daily' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Daily times (HH:MM, comma-separated)
                      </label>
                      <input
                        type="text"
                        value={inactiveDailyTimesText}
                        onChange={(e) => setInactiveDailyTimesText(e.target.value)}
                        placeholder="06:00, 14:00"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  {inactiveRefreshMode === 'interval' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Inactive refresh interval (seconds)
                      </label>
                      <input
                        type="number"
                        min="10"
                        value={inactiveInterval}
                        onChange={(e) => setInactiveInterval(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      When subscribers exist (active overlay)
                    </label>
                    <ResponsiveSelect
                      label="Active refresh mode"
                      value={activeRefreshMode}
                      onChange={(value) => setActiveRefreshMode(value as any)}
                      disabled={saving}
                      className="w-full"
                      options={[
                        { value: 'never', label: 'No extra refresh' },
                        { value: 'interval', label: 'Also refresh every X seconds' },
                      ]}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Active overlay runs on top of inactive policy.
                    </p>
                  </div>

                  {activeRefreshMode === 'interval' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Active refresh interval (seconds)
                      </label>
                      <input
                        type="number"
                        min="10"
                        value={activeInterval}
                        onChange={(e) => setActiveInterval(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Close
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

